import { FC, MouseEvent, useState } from "react";

import {
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  BarChartOutlined,
  ChevronRight,
  Delete,
  Edit,
  MoreVert,
  Output,
  Share,
} from "@mui/icons-material";

import { HelpBox } from "@/components/atoms";
import { Audience } from "@/types";

interface AudienceCardMenuProps {
  audience: Audience;
  onEditAudience?: (audience: Audience) => void;
  onDeleteAudience?: () => void;
  onShareAudience?: (audience: Audience) => void;
  onViewInsights?: (id: string | undefined) => void;
  onViewExport?: (id: string | undefined) => void;
}

export const AudienceCardMenu: FC<AudienceCardMenuProps> = ({
  audience,
  onEditAudience,
  onDeleteAudience,
  onShareAudience,
  onViewInsights,
  onViewExport,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [audienceSubmenuEl, setAudienceSubmenuEl] =
    useState<null | HTMLElement>(null);
  const [actionSubmenuEl, setActionSubmenuEl] = useState<null | HTMLElement>(
    null,
  );

  const showAudienceMenu =
    onEditAudience || onDeleteAudience || onShareAudience;
  const showActionMenu = onViewInsights || onViewExport;

  const handleOpenMenu = (event: MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setAudienceSubmenuEl(null);
    setActionSubmenuEl(null);
  };

  const handleOpenAudienceSubmenu = (event: MouseEvent<HTMLElement>) =>
    setAudienceSubmenuEl(event.currentTarget);

  const handleCloseAudienceSubmenu = () => setAudienceSubmenuEl(null);

  const handleOpenActionSubmenu = (event: MouseEvent<HTMLElement>) =>
    setActionSubmenuEl(event.currentTarget);

  const handleDeleteAudience = () => {
    if (onDeleteAudience) {
      onDeleteAudience();
      handleCloseMenu();
    }
  };
  const handleEditAudience = () => {
    if (onEditAudience) {
      onEditAudience(audience);
      handleCloseMenu();
    }
  };

  const handleShareAudience = () => {
    if (onShareAudience) {
      onShareAudience(audience);
      handleCloseMenu();
    }
  };

  const handleViewInsights = () => {
    if (onViewInsights) {
      onViewInsights(audience.audience_id);
      handleCloseMenu();
    }
  };

  const handleViewExport = () => {
    if (onViewExport) {
      onViewExport(audience.audience_id);
      handleCloseMenu();
    }
  };

  return (
    <>
      {/* Main Menu Button */}
      <HelpBox title="Audience Actions">
        <IconButton
          onClick={handleOpenMenu}
          sx={{ p: 0.5, borderRadius: "6px" }}
        >
          <MoreVert />
        </IconButton>
      </HelpBox>

      {/* Main Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        slotProps={{
          paper: { style: { width: 140 } },
        }}
      >
        {/* Audience Submenu */}
        {showAudienceMenu && (
          <>
            <MenuItem
              onClick={handleOpenAudienceSubmenu}
              disabled={audience.shared}
            >
              <ListItemText primary="Audience" />
              <ChevronRight />
            </MenuItem>

            {/* Audience Options - Edit, Delete & Share */}
            <Menu
              anchorEl={audienceSubmenuEl}
              open={Boolean(audienceSubmenuEl)}
              onClose={handleCloseAudienceSubmenu}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
              {onEditAudience && (
                <MenuItem onClick={handleEditAudience}>
                  <ListItemIcon>
                    <Edit color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Rename" />
                </MenuItem>
              )}
              {onDeleteAudience && (
                <MenuItem onClick={handleDeleteAudience}>
                  <ListItemIcon>
                    <Delete color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Delete" />
                </MenuItem>
              )}
              {onShareAudience && (
                <MenuItem onClick={handleShareAudience}>
                  <ListItemIcon>
                    <Share color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Share" />
                </MenuItem>
              )}
            </Menu>

            <Divider />
          </>
        )}

        {/* Actions Submenu */}
        {showActionMenu && (
          <>
            <MenuItem
              onClick={handleOpenActionSubmenu}
              disabled={!audience.audience_id}
            >
              <ListItemText primary="Actions" />
              <ChevronRight />
            </MenuItem>

            {/* Actions - Insights & Export */}
            <Menu
              anchorEl={actionSubmenuEl}
              open={Boolean(actionSubmenuEl)}
              onClose={handleCloseMenu}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
              {onViewInsights && (
                <MenuItem onClick={handleViewInsights}>
                  <ListItemIcon>
                    <BarChartOutlined color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Insights" />
                </MenuItem>
              )}
              {onViewExport && (
                <MenuItem onClick={handleViewExport}>
                  <ListItemIcon>
                    <Output color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Export" />
                </MenuItem>
              )}
            </Menu>
          </>
        )}
      </Menu>
    </>
  );
};
